import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import HelpOutlinedIcon from '@mui/icons-material/HelpOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormHelperText,
  Grid,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  chipClasses,
} from '@mui/material';
import { useRef, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useFeatureFlagsContext } from '../../contexts/featureFlagContext';
import { resolveClientEnv } from '../../graphql/config/resolveClientEnv';
import { BrokerageCanvasserDirectoryFailureNotice } from '../BrokerageCanvasserDirectoryFailureNotice';
import {
  useBrokerageCanvasserDirectory,
  type BrokerageCanvasserDirectoryContextValue,
} from '../BrokerageCanvasserDirectoryContext';
import { EmptyState } from '../EmptyState';
import { ErrorState } from '../ErrorState';
import { LoadingState } from '../LoadingState';
import { PageControlsBar } from '../PageControlsBar';
import { PageHeader } from '../PageHeader';
import { cloneDoorVisit } from '../cloneDoorVisitApi';
import { isNonBlankDoorVisitCoreString } from '../doorVisitCoreApi';
import { replayDoorVisit } from '../replayDoorVisitApi';
import { resolveVoterReturn } from '../voters/voterNavigation';
import { useDoorVisitDetail, type DoorVisitDetail, type UseDoorVisitDetailOptions } from '../useDoorVisitDetail';
import { useDoorVisitPlayback, type UseDoorVisitPlaybackResult } from '../useDoorVisitPlayback';
import {
  formatDoorVisitTranscriptTimestamp,
  useDoorVisitTranscript,
  type DoorVisitTranscriptState,
} from '../useDoorVisitTranscript';

// ─── Types ────────────────────────────────────────────────────────────────────

export type DoorVisitDetailPageProps = Pick<UseDoorVisitDetailOptions, 'fetchImpl'>;

// NonNullable unwrap: DoorVisitDetail['coreInsights'] is Maybe<{...}> because the
// generated scalar types keep it nullable. Sub-components that receive a confirmed
// non-null insights object use this alias so their prop signatures are precise.
type CoreInsights = NonNullable<DoorVisitDetail['coreInsights']>;

// ─── Helpers ──────────────────────────────────────────────────────────────────

// DateTime scalar is typed as `unknown` in the generated schema — String()
// normalises it safely before passing to Date.
const formatVisitTime = (iso: unknown): string => {
  const date = new Date(String(iso));
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const TRANSCRIPT_EMPTY_MESSAGE = 'No transcript available.';
const TRANSCRIPT_UNAVAILABLE_MESSAGE = 'Transcript is temporarily unavailable.';
const PLAYBACK_UNAVAILABLE_MESSAGE = 'Recording is temporarily unavailable.';
const UNKNOWN_CANVASSER_LABEL = 'Unknown';

type CanvasserNamePresentation = {
  text: string;
  label?: string;
};

const getCanvasserNamePresentation = (
  canvasserId: string | null | undefined,
  directory: BrokerageCanvasserDirectoryContextValue,
): CanvasserNamePresentation => {
  if (!isNonBlankDoorVisitCoreString(canvasserId)) {
    return { text: UNKNOWN_CANVASSER_LABEL };
  }

  if (directory.status === 'loading') {
    return { text: directory.resolveLabel(canvasserId), label: 'Loading canvasser name' };
  }

  if (directory.status === 'error') {
    return { text: directory.resolveLabel(canvasserId) };
  }

  const resolvedName = directory.resolveLabel(canvasserId).trim();
  return { text: resolvedName.length > 0 ? resolvedName : UNKNOWN_CANVASSER_LABEL };
};

type DetailHeaderMetadataProps = {
  visit: DoorVisitDetail;
};

const DetailHeaderMetadata = ({ visit }: DetailHeaderMetadataProps) => {
  const directory = useBrokerageCanvasserDirectory();
  const canvasserName = getCanvasserNamePresentation(visit.canvasserId, directory);
  const isMatched = visit.matchedHouseholdId !== null && visit.matchedHouseholdId !== undefined;

  return (
    <Stack spacing={0.5} sx={{ minWidth: 0 }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', flexWrap: 'wrap', minWidth: 0 }}>
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>
          {visit.address ?? 'Address unavailable'} &bull; Visited {formatVisitTime(visit.dwellStartedAt)}
        </Typography>
        {!isMatched && (
          <Chip
            label="No household matched"
            size="small"
            color="warning"
            variant="outlined"
            icon={<HelpOutlinedIcon />}
            data-testid="unmatched-chip"
          />
        )}
      </Stack>
      <Stack
        direction="row"
        spacing={1.5}
        data-testid="door-visit-identity-metadata"
        sx={{ flexWrap: 'wrap', overflowWrap: 'anywhere', minWidth: 0 }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
          Canvasser:{' '}
          <Typography
            component="span"
            variant="body2"
            color="text.primary"
            aria-label={canvasserName.label}
            sx={{ overflowWrap: 'anywhere' }}
          >
            {canvasserName.text}
          </Typography>
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere', minWidth: 0 }}>
          Canvassing session:{' '}
          <MuiLink
            component={RouterLink}
            to={`/canvassing-sessions/${visit.sessionId}`}
            underline="hover"
            sx={{ overflowWrap: 'anywhere' }}
          >
            {visit.sessionId}
          </MuiLink>
        </Typography>
      </Stack>
    </Stack>
  );
};

// ─── Sub-components ───────────────────────────────────────────────────────────

type SectionCardProps = {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
};

const SectionCard = ({ title, subtitle, children }: SectionCardProps) => (
  <Paper variant="outlined" sx={{ p: 3 }}>
    <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: subtitle ? 0.5 : 2 }}>
      {title}
    </Typography>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {subtitle}
      </Typography>
    )}
    {children}
  </Paper>
);

type StringChipListProps = {
  ariaLabel: string;
  items: readonly string[];
};

const StringChipList = ({ ariaLabel, items }: StringChipListProps) => (
  <Box
    component="ul"
    aria-label={ariaLabel}
    sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, listStyle: 'none', m: 0, p: 0 }}
  >
    {items.map(item => (
      <Chip
        component="li"
        key={item}
        label={item}
        size="small"
        color="primary"
        variant="outlined"
        sx={{
          maxWidth: '100%',
          minWidth: 0,
          height: 'auto',
          minHeight: 24,
          whiteSpace: 'normal',
          [`& .${chipClasses.label}`]: {
            overflow: 'visible',
            textOverflow: 'clip',
            whiteSpace: 'normal',
            overflowWrap: 'anywhere',
          },
        }}
      />
    ))}
  </Box>
);

type ConversationSummaryCardProps = {
  summary: CoreInsights['conversationSummary'];
};

const ConversationSummaryCard = ({ summary }: ConversationSummaryCardProps) => (
  <SectionCard title="Conversation summary">
    {summary ? (
      <Typography variant="body2">{summary}</Typography>
    ) : (
      <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
        No summary recorded.
      </Typography>
    )}
  </SectionCard>
);

type CandidateSentimentsCardProps = {
  sentiments: CoreInsights['candidateSentiments'];
};

const CandidateSentimentsCard = ({ sentiments }: CandidateSentimentsCardProps) => (
  <SectionCard title="Sentiment toward the candidate" subtitle="Sentiment toward the candidate and opponent">
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Candidate</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Sentiment</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sentiments.map(row => (
            <TableRow key={row.candidateName}>
              <TableCell>{row.candidateName}</TableCell>
              <TableCell>{row.sentiment}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  </SectionCard>
);

type ObservationsCardProps = {
  observations: CoreInsights['observations'];
};

const ObservationsCard = ({ observations }: ObservationsCardProps) => (
  <SectionCard title="Observed at the door">
    <StringChipList ariaLabel="Observed at the door" items={observations} />
  </SectionCard>
);

type ConversationTagsCardProps = {
  conversationalTags: CoreInsights['conversationalTags'];
};

const ConversationTagsCard = ({ conversationalTags }: ConversationTagsCardProps) => (
  <SectionCard title="Conversation tags">
    <StringChipList ariaLabel="Conversation tags" items={conversationalTags} />
  </SectionCard>
);

type TranscriptCardProps = {
  state: DoorVisitTranscriptState;
  onRetry: () => void;
};

const TranscriptCard = ({ state, onRetry }: TranscriptCardProps) => (
  <SectionCard title="Transcript">
    {state.status === 'loading' && (
      <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center' }}>
        <CircularProgress size={18} aria-label="Loading transcript" />
        <Typography variant="body2" color="text.secondary">
          Loading transcript…
        </Typography>
      </Stack>
    )}
    {state.status === 'error' && (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={onRetry}>
            Retry transcript
          </Button>
        }
      >
        {TRANSCRIPT_UNAVAILABLE_MESSAGE}
      </Alert>
    )}
    {state.status === 'ready' && (
      <>
        {state.refreshing && (
          <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center', mb: 1 }}>
            <CircularProgress size={16} aria-label="Refreshing transcript" />
            <Typography variant="body2" color="text.secondary">
              Refreshing transcript…
            </Typography>
          </Stack>
        )}
        {state.events.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            {TRANSCRIPT_EMPTY_MESSAGE}
          </Typography>
        ) : (
          <Box
            role="region"
            tabIndex={0}
            aria-label="Transcript"
            sx={{
              maxHeight: '30rem',
              overflowY: 'auto',
              pr: 1,
              outlineOffset: 2,
            }}
          >
            <Stack spacing={2}>
              {state.events.map((event, index) => (
                <Box key={`${String(event.occurredAt)}-${index}`}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
                    <Typography variant="subtitle2">{event.speaker}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatDoorVisitTranscriptTimestamp(event.occurredAt)}
                    </Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ mt: 0.5, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                    {event.transcript}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </>
    )}
  </SectionCard>
);

type IssuesCardProps = {
  customSlots: CoreInsights['customSlots'];
};

const IssuesCard = ({ customSlots }: IssuesCardProps) => (
  <SectionCard title="Issues" subtitle="Positions on key policy questions">
    <TableContainer>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Tag</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Voter position</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {customSlots.length > 0 ? (
            customSlots.map(slot => (
              <TableRow key={slot.key}>
                <TableCell sx={{ fontWeight: 500 }}>{slot.label}</TableCell>
                <TableCell>{slot.valueLabel}</TableCell>
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={2} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                No voter positions recorded.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  </SectionCard>
);

// ─── Pending Insights Panel ───────────────────────────────────────────────────

const PendingInsightsPanel = () => (
  <Alert severity="info" data-testid="pending-insights-panel">
    Core insights have not yet arrived for this visit. Check back after the session has been processed.
  </Alert>
);

// ─── Clone / Replay Confirm Actions ───────────────────────────────────────────

type DoorVisitMutationConfirmActionProps = {
  hasClip: boolean;
  startIcon: React.ReactNode;
  buttonLabel: string;
  dialogTitle: string;
  dialogDescription: string;
  dialogTitleId: string;
  dialogDescriptionId: string;
  unavailableDescriptionId: string;
  loadingStatusLabel: string;
  submittingButtonLabel: string;
  retryButtonLabel: string;
  fallbackError: string;
  runMutation: (requestId: string) => Promise<void>;
  onSuccess?: () => void;
};

const DoorVisitMutationConfirmAction = ({
  hasClip,
  startIcon,
  buttonLabel,
  dialogTitle,
  dialogDescription,
  dialogTitleId,
  dialogDescriptionId,
  unavailableDescriptionId,
  loadingStatusLabel,
  submittingButtonLabel,
  retryButtonLabel,
  fallbackError,
  runMutation,
  onSuccess,
}: DoorVisitMutationConfirmActionProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [requestError, setRequestError] = useState<string | null>(null);
  const requestIdRef = useRef<string | null>(null);
  const submissionInFlightRef = useRef(false);

  const openDialog = (): void => {
    setRequestError(null);
    setDialogOpen(true);
  };

  const closeDialog = (): void => {
    if (submissionInFlightRef.current) {
      return;
    }

    setRequestError(null);
    setDialogOpen(false);
  };

  const confirmAction = async (): Promise<void> => {
    if (submissionInFlightRef.current) {
      return;
    }

    const requestId = requestIdRef.current ?? crypto.randomUUID();
    requestIdRef.current = requestId;
    submissionInFlightRef.current = true;
    setRequestError(null);
    setIsSubmitting(true);

    try {
      await runMutation(requestId);
      // A successful response confirms this idempotency key. Failed attempts keep
      // it through dialog close/reopen so an uncertain transport failure replays
      // the same server-side request rather than creating another mutation.
      requestIdRef.current = null;
      setDialogOpen(false);
      onSuccess?.();
    } catch (error) {
      setRequestError(error instanceof Error ? error.message : fallbackError);
    } finally {
      submissionInFlightRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
      <Button
        variant="outlined"
        startIcon={startIcon}
        disabled={!hasClip}
        aria-describedby={!hasClip ? unavailableDescriptionId : undefined}
        onClick={openDialog}
        sx={{ width: { xs: '100%', sm: 'auto' } }}
      >
        {buttonLabel}
      </Button>
      {!hasClip && <FormHelperText id={unavailableDescriptionId}>No audio clip available</FormHelperText>}

      <Dialog
        open={dialogOpen}
        onClose={closeDialog}
        aria-labelledby={dialogTitleId}
        aria-describedby={dialogDescriptionId}
        aria-busy={isSubmitting}
      >
        <DialogTitle id={dialogTitleId}>{dialogTitle}</DialogTitle>
        <DialogContent>
          <DialogContentText id={dialogDescriptionId}>{dialogDescription}</DialogContentText>
          {isSubmitting && (
            <Stack direction="row" spacing={1} role="status" aria-live="polite" sx={{ alignItems: 'center', mt: 2 }}>
              <CircularProgress size={20} aria-label={buttonLabel} />
              <Typography variant="body2">{loadingStatusLabel}</Typography>
            </Stack>
          )}
          {requestError !== null && (
            <Alert severity="error" sx={{ mt: 2 }} aria-live="assertive">
              {requestError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={closeDialog} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button variant="contained" onClick={() => void confirmAction()} disabled={isSubmitting}>
            {isSubmitting ? submittingButtonLabel : requestError === null ? buttonLabel : retryButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

type CloneDoorVisitActionProps = {
  doorVisitId: number;
  hasClip: boolean;
  fetchImpl?: DoorVisitDetailPageProps['fetchImpl'];
};

const CloneDoorVisitAction = ({ doorVisitId, hasClip, fetchImpl }: CloneDoorVisitActionProps) => {
  const navigate = useNavigate();

  return (
    <DoorVisitMutationConfirmAction
      hasClip={hasClip}
      startIcon={<ContentCopyIcon />}
      buttonLabel="Clone door visit"
      dialogTitle="Clone door visit?"
      dialogDescription="This creates a new Door Visit, sends the existing recording to Core, and does not change the original."
      dialogTitleId="clone-door-visit-dialog-title"
      dialogDescriptionId="clone-door-visit-dialog-description"
      unavailableDescriptionId={`door-visit-${doorVisitId}-clone-audio-unavailable`}
      loadingStatusLabel="Clone door visit…"
      submittingButtonLabel="Cloning…"
      retryButtonLabel="Retry cloning"
      fallbackError="Unable to clone this door visit."
      runMutation={async requestId => {
        const cloneId = await cloneDoorVisit(doorVisitId, requestId, {
          env: resolveClientEnv(),
          fetchImpl,
        });
        void navigate(`/door-visits/${cloneId}`);
      }}
    />
  );
};

type ReplayDoorVisitActionProps = {
  doorVisitId: number;
  hasClip: boolean;
  fetchImpl?: DoorVisitDetailPageProps['fetchImpl'];
  onReplaySuccess: () => void;
};

type DoorVisitPlaybackControlsProps = {
  playback: UseDoorVisitPlaybackResult;
};

const DoorVisitPlaybackControls = ({ playback }: DoorVisitPlaybackControlsProps) => {
  if (!playback.eligible) return null;

  return (
    <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0, flex: '1 1 100%' }}>
      {playback.state.status === 'ready' ? (
        <audio
          ref={playback.bindAudio}
          controls
          src={playback.state.playbackUrl}
          aria-label="Recording playback"
          onError={playback.reportMediaError}
          style={{ display: 'block', width: '100%', maxWidth: '100%' }}
        />
      ) : playback.state.status === 'unavailable' ? (
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={playback.requestPlayback}>
              Retry recording
            </Button>
          }
        >
          {PLAYBACK_UNAVAILABLE_MESSAGE}
        </Alert>
      ) : (
        <Stack spacing={0.5} sx={{ minWidth: 0, maxWidth: '100%' }}>
          <Button
            variant="outlined"
            disabled={playback.state.status === 'loading'}
            onClick={playback.requestPlayback}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Play recording
          </Button>
          {playback.state.status === 'loading' && (
            <Typography role="status" aria-live="polite" variant="body2" color="text.secondary">
              Loading recording…
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
};

const ReplayDoorVisitAction = ({ doorVisitId, hasClip, fetchImpl, onReplaySuccess }: ReplayDoorVisitActionProps) => (
  <DoorVisitMutationConfirmAction
    hasClip={hasClip}
    startIcon={<ReplayIcon />}
    buttonLabel="Replay door visit"
    dialogTitle="Replay door visit?"
    dialogDescription="This replaces this visit’s Core results, deletes the Field Ops copy of the old call, and any other visit that pointed at that call loses that join. New slots arrive after Core reprocesses."
    dialogTitleId="replay-door-visit-dialog-title"
    dialogDescriptionId="replay-door-visit-dialog-description"
    unavailableDescriptionId={`door-visit-${doorVisitId}-replay-audio-unavailable`}
    loadingStatusLabel="Replay door visit…"
    submittingButtonLabel="Replaying…"
    retryButtonLabel="Retry replay"
    fallbackError="Unable to replay this door visit."
    runMutation={async requestId => {
      await replayDoorVisit(doorVisitId, requestId, {
        env: resolveClientEnv(),
        fetchImpl,
      });
    }}
    onSuccess={onReplaySuccess}
  />
);

// ─── Populated View ───────────────────────────────────────────────────────────

type PopulatedViewProps = {
  visit: DoorVisitDetail;
  backgroundLoading: boolean;
  backgroundError: string | null;
  onRefresh: () => void;
  transcript: React.ReactNode;
};

const PopulatedView = ({ visit, backgroundLoading, backgroundError, onRefresh, transcript }: PopulatedViewProps) => {
  // Extract to a local variable so TypeScript can narrow non-null inside the
  // truthy JSX branch — conditional JSX alone does not narrow object properties.
  const insights = visit.coreInsights ?? null;

  return (
    <Box>
      {backgroundLoading && (
        <Stack role="status" aria-live="polite" direction="row" spacing={1} sx={{ alignItems: 'center', mb: 2 }}>
          <CircularProgress size={18} aria-label="Refreshing door visit details" />
          <Typography variant="body2" color="text.secondary">
            Refreshing door visit details…
          </Typography>
        </Stack>
      )}
      {backgroundError !== null && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={onRefresh} disabled={backgroundLoading}>
              Refresh
            </Button>
          }
        >
          Couldn&apos;t refresh this door visit. Displayed details may be incomplete.
        </Alert>
      )}

      {/* Two-column insight grid or pending panel */}
      {insights !== null ? (
        <Grid container spacing={3}>
          {/* Left column: summary + sentiments + observations + tags + transcript */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2}>
              <ConversationSummaryCard summary={insights.conversationSummary} />
              {insights.candidateSentiments.length > 0 && (
                <CandidateSentimentsCard sentiments={insights.candidateSentiments} />
              )}
              {insights.observations.length > 0 && <ObservationsCard observations={insights.observations} />}
              {insights.conversationalTags.length > 0 && (
                <ConversationTagsCard conversationalTags={insights.conversationalTags} />
              )}
              {transcript}
            </Stack>
          </Grid>

          {/* Right column: policy positions */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Stack spacing={2}>
              <IssuesCard customSlots={insights.customSlots} />
            </Stack>
          </Grid>
        </Grid>
      ) : (
        <Stack spacing={2}>
          <PendingInsightsPanel />
          {transcript}
        </Stack>
      )}
    </Box>
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

export const DoorVisitDetailPage = ({ fetchImpl }: DoorVisitDetailPageProps) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const { isStructurelyAdmin } = useFeatureFlagsContext();
  const doorVisitId = Number(id);
  const voterReturn = resolveVoterReturn(location.state);

  const { data, loading, error, backgroundLoading, backgroundError, refetch } = useDoorVisitDetail({ doorVisitId, fetchImpl });
  const currentVisit = data !== null && data.doorVisitId === doorVisitId ? data : null;
  const playback = useDoorVisitPlayback({
    routeDoorVisitId: doorVisitId,
    detail: currentVisit,
    fetchImpl,
  });
  const { state: transcriptState, retry: retryTranscript } = useDoorVisitTranscript({
    routeDoorVisitId: doorVisitId,
    detail: currentVisit,
    fetchImpl,
  });
  const headerTitle = currentVisit === null ? 'Visit Details' : currentVisit.voterName ?? 'Unknown voter';
  const hasTranscript =
    currentVisit !== null &&
    isNonBlankDoorVisitCoreString(currentVisit.coreLeadId) &&
    isNonBlankDoorVisitCoreString(currentVisit.coreCallId);
  const transcript = hasTranscript ? <TranscriptCard state={transcriptState} onRetry={retryTranscript} /> : null;

  return (
    <>
      <PageHeader
        title={headerTitle}
        backLink={
          voterReturn === null
            ? { label: 'Door Visits', to: '/door-visits' }
            : { label: 'Voter', to: voterReturn.to, ...(voterReturn.state === undefined ? {} : { state: voterReturn.state }) }
        }
        metadata={currentVisit === null ? undefined : <DetailHeaderMetadata visit={currentVisit} />}
      />
      {currentVisit !== null && (playback.eligible || isStructurelyAdmin) && (
        <PageControlsBar label="Visit actions">
          <DoorVisitPlaybackControls playback={playback} />
          {isStructurelyAdmin && (
            <>
              <CloneDoorVisitAction
                doorVisitId={currentVisit.doorVisitId}
                hasClip={currentVisit.hasClip}
                fetchImpl={fetchImpl}
              />
              <ReplayDoorVisitAction
                doorVisitId={currentVisit.doorVisitId}
                hasClip={currentVisit.hasClip}
                fetchImpl={fetchImpl}
                onReplaySuccess={refetch}
              />
            </>
          )}
        </PageControlsBar>
      )}
      {loading || (data !== null && currentVisit === null) ? (
        <LoadingState />
      ) : error !== null ? (
        <ErrorState message={error} onRetry={refetch} />
      ) : data === null ? (
        <EmptyState
          title="Visit not found"
          hint="This visit does not exist or you do not have access to it."
        />
      ) : (
        <>
          <BrokerageCanvasserDirectoryFailureNotice />
          <PopulatedView
            visit={data}
            backgroundLoading={backgroundLoading}
            backgroundError={backgroundError}
            onRefresh={refetch}
            transcript={transcript}
          />
        </>
      )}
    </>
  );
};
